import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  ArrowRightLeft,
  Ban,
  Check,
  ChefHat,
  Clock3,
  GripVertical,
  Minus,
  NotebookPen,
  Percent,
  Plus,
  Trash2,
  Wine,
} from 'lucide-react';
import {
  COURSE_LABELS,
  formatRate,
  type Order,
  type OrderItem,
  type OrderItemStatus,
} from '@sebastian/shared';
import { formatClock } from '../lib/format';
import { useCurrency } from '../lib/currency';
import { Badge } from './ui';

const STATUS_META: Record<OrderItemStatus, { label: string; className: string }> = {
  DRAFT: { label: 'Not sent', className: 'text-caution' },
  SENT: { label: 'Sent', className: 'text-ink-faint' },
  PREPARING: { label: 'Preparing', className: 'text-reserved' },
  READY: { label: 'Ready', className: 'text-positive' },
  SERVED: { label: 'Served', className: 'text-ink-faint' },
  CANCELLED: { label: 'Cancelled', className: 'text-danger' },
};

export interface OrderPanelHandlers {
  onToggleSelect: (itemId: string) => void;
  onChangeQuantity: (item: OrderItem, next: number) => void;
  onRemove: (item: OrderItem) => void;
  onCancel: (item: OrderItem) => void;
  onEditNote: (item: OrderItem) => void;
  onMoveCourse: (item: OrderItem, course: number) => void;
  /** Persist a new firing priority for the unsent items in one course. */
  onReorder: (course: number, itemIds: string[]) => void;
  /** Open the "move to another table" dialog for this line — draft or already fired. */
  onMoveTable: (item: OrderItem) => void;
  /** Money off this one line. Only offered to roles that may discount. */
  onDiscount?: (item: OrderItem) => void;
}

function ItemRow({
  item,
  selected,
  handlers,
  dragHandle,
  dragging,
  rowRef,
}: {
  item: OrderItem;
  selected: boolean;
  handlers: OrderPanelHandlers;
  /** Present only for unsent items, which are the only ones that can be reprioritised. */
  dragHandle?: {
    onPointerDown: (event: React.PointerEvent<HTMLButtonElement>) => void;
  };
  dragging?: boolean;
  rowRef?: (el: HTMLDivElement | null) => void;
}) {
  const { money } = useCurrency();
  const meta = STATUS_META[item.status];
  const isDraft = item.status === 'DRAFT';
  const isCancelled = item.status === 'CANCELLED';

  return (
    <div
      ref={rowRef}
      className={clsx(
        'rounded-lg border px-3 py-2.5 transition',
        isCancelled && 'border-line/60 bg-transparent opacity-50',
        !isCancelled && isDraft && selected && 'border-accent/60 bg-accent/8',
        !isCancelled && isDraft && !selected && 'border-line bg-canvas',
        !isCancelled && !isDraft && 'border-line/70 bg-surface',
        // The row left behind at its original spot while its ghost follows the pointer.
        dragging && 'pointer-events-none opacity-30',
      )}
    >
      <div className="flex items-start gap-2.5">
        {dragHandle ? (
          <button
            type="button"
            aria-label="Drag to reorder"
            onPointerDown={dragHandle.onPointerDown}
            className="-my-2 -ml-1.5 flex h-10 w-9 shrink-0 cursor-grab touch-none items-center justify-center rounded-md text-ink-faint transition hover:bg-hover hover:text-accent active:cursor-grabbing active:bg-accent/10"
          >
            <GripVertical size={16} />
          </button>
        ) : null}
        {isDraft ? (
          <button
            type="button"
            aria-label={selected ? 'Deselect item' : 'Select item to send'}
            onClick={() => handlers.onToggleSelect(item.id)}
            className={clsx(
              'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition',
              selected ? 'border-accent bg-accent text-white' : 'border-line-strong',
            )}
          >
            {selected ? <Check size={13} strokeWidth={3} /> : null}
          </button>
        ) : (
          <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center">
            {item.destination === 'BAR' ? (
              <Wine size={14} className="text-ink-faint" />
            ) : item.destination === 'KITCHEN' ? (
              <ChefHat size={14} className="text-ink-faint" />
            ) : null}
          </span>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <span
              className={clsx(
                'text-sm font-medium leading-snug',
                isCancelled ? 'text-ink-faint line-through' : 'text-ink',
              )}
            >
              <span className="tnum text-accent">{item.quantity}×</span> {item.nameSnapshot}
            </span>
            <span className="shrink-0 text-right">
              <span
                className={clsx(
                  'tnum block text-sm',
                  item.lineItemDiscountCents > 0 ? 'text-ink-faint line-through' : 'text-ink-soft',
                )}
              >
                {money(item.lineSubtotalCents)}
              </span>
              {item.lineItemDiscountCents > 0 ? (
                <span className="tnum block text-sm text-positive">
                  {money(item.lineSubtotalCents - item.lineItemDiscountCents)}
                </span>
              ) : null}
            </span>
          </div>

          {item.modifiers.length > 0 ? (
            <div className="mt-0.5 text-[11px] leading-snug text-ink-faint">
              {item.modifiers
                .map((modifier) =>
                  modifier.quantity > 1
                    ? `${modifier.quantity}× ${modifier.optionName}`
                    : modifier.optionName,
                )
                .join(' · ')}
            </div>
          ) : null}

          {item.notes ? (
            <div className="mt-1 flex items-start gap-1 text-[11px] italic leading-snug text-caution">
              <NotebookPen size={11} className="mt-0.5 shrink-0" />
              {item.notes}
            </div>
          ) : null}

          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className={clsx('text-[11px] font-semibold uppercase tracking-wider', meta.className)}>
              {meta.label}
            </span>
            {item.sentAt ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-ink-faint">
                <Clock3 size={10} /> {formatClock(item.sentAt)}
                {item.ticketNumber ? ` · #${item.ticketNumber}` : ''}
              </span>
            ) : null}
            {item.cancelledReason ? (
              <span className="text-[11px] text-danger">{item.cancelledReason}</span>
            ) : null}
            {item.discountType !== null ? (
              <span className="text-[11px] font-medium text-positive">
                {item.lineItemDiscountCents >= item.lineSubtotalCents
                  ? 'On the house'
                  : `−${item.discountType === 'PERCENT' ? `${formatRate(item.discountValue)}%` : money(item.lineItemDiscountCents)}`}
                {item.discountReason ? ` · ${item.discountReason}` : ''}
              </span>
            ) : null}
            {item.taxType === 'ALCOHOL' ? (
              <span className="text-[10px] uppercase tracking-wider text-ink-faint">
                Alc · {formatRate(item.taxRateBp)}%
              </span>
            ) : null}
            {item.paidQuantity > 0 ? (
              <Badge tone={item.paidQuantity >= item.quantity ? 'positive' : 'caution'}>
                {item.paidQuantity >= item.quantity ? 'Paid' : `Paid ${item.paidQuantity}/${item.quantity}`}
              </Badge>
            ) : null}
          </div>

          {isDraft ? (
            <div className="mt-2 flex items-center gap-1.5">
              <button
                type="button"
                aria-label="Reduce quantity"
                onClick={() => handlers.onChangeQuantity(item, item.quantity - 1)}
                className="flex h-8 w-8 items-center justify-center rounded border border-line text-ink-soft transition hover:border-line-strong hover:text-ink"
              >
                <Minus size={14} />
              </button>
              <span className="tnum w-6 text-center text-sm text-ink">{item.quantity}</span>
              <button
                type="button"
                aria-label="Increase quantity"
                onClick={() => handlers.onChangeQuantity(item, item.quantity + 1)}
                className="flex h-8 w-8 items-center justify-center rounded border border-line text-ink-soft transition hover:border-line-strong hover:text-ink"
              >
                <Plus size={14} />
              </button>
              <button
                type="button"
                aria-label="Edit note"
                onClick={() => handlers.onEditNote(item)}
                className="ml-1 flex h-8 w-8 items-center justify-center rounded border border-line text-ink-soft transition hover:border-accent/50 hover:text-accent"
              >
                <NotebookPen size={14} />
              </button>
              <select
                aria-label="Move to course"
                value={item.course}
                onChange={(event) => handlers.onMoveCourse(item, Number(event.target.value))}
                className="h-8 rounded border border-line bg-canvas px-2 text-xs text-ink-soft"
              >
                {Object.entries(COURSE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              {handlers.onDiscount ? (
                <button
                  type="button"
                  aria-label="Discount this item"
                  onClick={() => handlers.onDiscount!(item)}
                  className="ml-auto flex h-8 w-8 items-center justify-center rounded border border-line text-ink-faint transition hover:border-positive/50 hover:text-positive"
                >
                  <Percent size={14} />
                </button>
              ) : null}
              <button
                type="button"
                aria-label="Move to another table"
                onClick={() => handlers.onMoveTable(item)}
                className={clsx(
                  'flex h-8 w-8 items-center justify-center rounded border border-line text-ink-faint transition hover:border-accent/50 hover:text-accent',
                  handlers.onDiscount ? '' : 'ml-auto',
                )}
              >
                <ArrowRightLeft size={14} />
              </button>
              <button
                type="button"
                aria-label="Remove item"
                onClick={() => handlers.onRemove(item)}
                className="flex h-8 w-8 items-center justify-center rounded border border-line text-ink-faint transition hover:border-danger/50 hover:text-danger"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ) : !isCancelled ? (
            <div className="mt-2 flex items-center gap-3">
              <button
                type="button"
                onClick={() => handlers.onMoveTable(item)}
                className="inline-flex items-center gap-1 text-[11px] text-ink-faint transition hover:text-accent"
              >
                <ArrowRightLeft size={11} /> Move
              </button>
              {handlers.onDiscount ? (
                <button
                  type="button"
                  onClick={() => handlers.onDiscount!(item)}
                  className="inline-flex items-center gap-1 text-[11px] text-ink-faint transition hover:text-positive"
                >
                  <Percent size={11} /> {item.discountType ? 'Change discount' : 'Discount'}
                </button>
              ) : null}
              {item.status !== 'SERVED' ? (
                <button
                  type="button"
                  onClick={() => handlers.onCancel(item)}
                  className="inline-flex items-center gap-1 text-[11px] text-ink-faint transition hover:text-danger"
                >
                  <Ban size={11} /> Void this item
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * The unsent lines of one course, reorderable by dragging — the priority set
 * here is exactly the order items land on the kitchen or bar ticket once sent
 * (see `sortOrder` on the order item), so this is how staff tell the kitchen
 * what to start on first.
 */
interface DragState {
  id: string;
  /** clientY at pickup, so later moves can compute how far the pointer has travelled. */
  startClientY: number;
  /** The dragged row's top offset within the list container, captured once at pickup. */
  originTop: number;
  /** How far the pointer has moved from where the drag started. */
  translateY: number;
  /** Index in `order` the item would land at if dropped right now. */
  dropIndex: number;
}

function DraftItemList({
  course,
  items,
  selectedIds,
  handlers,
}: {
  course: number;
  items: OrderItem[];
  selectedIds: Set<string>;
  handlers: OrderPanelHandlers;
}) {
  const [order, setOrder] = useState(() => items.map((item) => item.id));
  const orderRef = useRef(order);
  orderRef.current = order;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  // Mirrors `drag` state but read synchronously from the window listeners, which
  // close over whichever render was active when the gesture started — state
  // updates from later renders never reach them, so the live values live here.
  const dragInfo = useRef<DragState | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);

  // Re-sync when items are added/removed/sent, keeping the existing sequence
  // for ids that survive so an in-progress drag never gets reset mid-gesture.
  useEffect(() => {
    if (dragInfo.current) return;
    setOrder((current) => {
      const ids = items.map((item) => item.id);
      const idSet = new Set(ids);
      const kept = current.filter((id) => idSet.has(id));
      const added = ids.filter((id) => !kept.includes(id));
      const next = [...kept, ...added];
      return next.length === current.length && next.every((id, i) => id === current[i])
        ? current
        : next;
    });
  }, [items]);

  // Leaving mid-drag (e.g. navigating away) must not leak window listeners —
  // `detachRef` always points at whichever pair is actually attached right now.
  const detachRef = useRef<(() => void) | null>(null);
  useEffect(() => () => detachRef.current?.(), []);

  const itemsById = new Map(items.map((item) => [item.id, item]));

  function handlePointerMove(event: PointerEvent) {
    const info = dragInfo.current;
    if (!info) return;
    event.preventDefault();
    const currentOrder = orderRef.current;
    const y = event.clientY;
    let dropIndex = currentOrder.length;
    for (let i = 0; i < currentOrder.length; i++) {
      const id = currentOrder[i]!;
      if (id === info.id) continue;
      const el = rowRefs.current.get(id);
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (y < rect.top + rect.height / 2) {
        dropIndex = i;
        break;
      }
    }
    const next: DragState = { ...info, translateY: y - info.startClientY, dropIndex };
    dragInfo.current = next;
    setDrag(next);
  }

  function endDrag() {
    detachRef.current?.();
    detachRef.current = null;
    const info = dragInfo.current;
    dragInfo.current = null;
    setDrag(null);
    if (!info) return;

    const from = orderRef.current.indexOf(info.id);
    let to = info.dropIndex;
    if (from === -1 || from === to || from + 1 === to) return; // no real move
    if (from < to) to -= 1;
    const next = [...orderRef.current];
    next.splice(from, 1);
    next.splice(to, 0, info.id);
    setOrder(next);
    handlers.onReorder(course, next);
  }

  function startDrag(itemId: string) {
    return (event: React.PointerEvent<HTMLButtonElement>) => {
      event.preventDefault();
      const row = rowRefs.current.get(itemId);
      const container = containerRef.current;
      if (!row || !container) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      const rowRect = row.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const initial: DragState = {
        id: itemId,
        startClientY: event.clientY,
        originTop: rowRect.top - containerRect.top,
        translateY: 0,
        dropIndex: orderRef.current.indexOf(itemId),
      };
      dragInfo.current = initial;
      setDrag(initial);
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
      detachRef.current = () => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', endDrag);
        window.removeEventListener('pointercancel', endDrag);
      };
    };
  }

  const draggedIndex = drag ? order.indexOf(drag.id) : -1;
  // Dropping right back where it started (or the slot immediately after) is a no-op.
  const dropIsNoop = drag ? drag.dropIndex === draggedIndex || drag.dropIndex === draggedIndex + 1 : true;

  return (
    <div ref={containerRef} className="relative space-y-2">
      {order.map((itemId, index) => {
        const item = itemsById.get(itemId);
        if (!item) return null;
        const isDragging = drag?.id === itemId;
        const showDropLineBefore = !dropIsNoop && !isDragging && drag?.dropIndex === index;
        return (
          <div key={item.id} className="relative">
            {showDropLineBefore ? <DropLine /> : null}
            <ItemRow
              item={item}
              selected={selectedIds.has(item.id)}
              handlers={handlers}
              dragHandle={{ onPointerDown: startDrag(item.id) }}
              dragging={isDragging}
              rowRef={(el) => {
                if (el) rowRefs.current.set(item.id, el);
                else rowRefs.current.delete(item.id);
              }}
            />
          </div>
        );
      })}
      {!dropIsNoop && drag && drag.dropIndex === order.length ? (
        <div className="relative h-0">
          <DropLine />
        </div>
      ) : null}

      {/* The ghost: a lifted copy that tracks the pointer, so touch and mouse
          both get a clear answer to "what am I moving, and where will it land". */}
      {drag ? (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-20 shadow-xl"
          style={{ transform: `translateY(${drag.originTop + drag.translateY}px) rotate(1deg) scale(1.02)` }}
        >
          <ItemRow
            item={itemsById.get(drag.id)!}
            selected={selectedIds.has(drag.id)}
            handlers={handlers}
          />
        </div>
      ) : null}
    </div>
  );
}

/** The accent insertion line shown between rows while dragging. */
function DropLine() {
  return (
    <div className="pointer-events-none absolute -top-1.25 inset-x-0 z-10 h-0.75 rounded-full bg-accent-strong shadow-[0_0_8px_rgba(212,163,79,0.7)]" />
  );
}

/** Order contents grouped by course, in service order. */
export function OrderPanel({
  order,
  selectedIds,
  handlers,
  onSelectCourse,
}: {
  order: Order;
  selectedIds: Set<string>;
  handlers: OrderPanelHandlers;
  onSelectCourse: (course: number, itemIds: string[]) => void;
}) {
  const byCourse = new Map<number, OrderItem[]>();
  for (const item of order.items) {
    byCourse.set(item.course, [...(byCourse.get(item.course) ?? []), item]);
  }
  const courses = [...byCourse.keys()].sort((a, b) => a - b);

  return (
    <div className="space-y-5">
      {courses.map((course) => {
        const items = byCourse.get(course)!;
        const firedItems = items.filter((item) => item.status !== 'DRAFT');
        const draftItems = items.filter((item) => item.status === 'DRAFT');
        const draftIds = draftItems.map((item) => item.id);
        const allSelected = draftIds.length > 0 && draftIds.every((id) => selectedIds.has(id));

        return (
          <section key={course}>
            <div className="mb-2 flex items-center gap-2">
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">
                {COURSE_LABELS[course]}
              </h3>
              <div className="rule-accent flex-1 opacity-40" />
              {draftIds.length > 0 ? (
                <button
                  type="button"
                  onClick={() => onSelectCourse(course, draftIds)}
                  className="text-[11px] font-medium text-ink-faint transition hover:text-accent"
                >
                  {allSelected ? 'Clear' : 'Select all'}
                </button>
              ) : null}
            </div>
            {firedItems.length > 0 ? (
              <div className="mb-2 space-y-2">
                {firedItems.map((item) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    selected={selectedIds.has(item.id)}
                    handlers={handlers}
                  />
                ))}
              </div>
            ) : null}
            {draftItems.length > 1 ? (
              <div className="mb-1.5 flex items-center gap-1 text-[10px] text-ink-faint">
                <GripVertical size={11} /> Drag to set kitchen priority
              </div>
            ) : null}
            {draftItems.length > 0 ? (
              <DraftItemList
                course={course}
                items={draftItems}
                selectedIds={selectedIds}
                handlers={handlers}
              />
            ) : null}
          </section>
        );
      })}

      {order.notes ? (
        <div className="rounded-lg border border-line bg-canvas px-3 py-2.5">
          <Badge tone="accent">Table note</Badge>
          <p className="mt-2 text-sm leading-snug text-ink-soft">{order.notes}</p>
        </div>
      ) : null}
    </div>
  );
}
